/**
 * Botões de ação dependentes do `status` do aluno:
 *   - waiting → "Liberar app para o aluno" (verde, grande)
 *   - active  → "Inativar aluno" (link cinza, discreto)
 *   - inactive → nada (orquestrador não monta o componente)
 *
 * Props:
 *   - status: 'waiting' | 'active' | 'inactive'
 *   - activating: flag de loading (vem do orquestrador, partilhada
 *     entre handleActivate e handleInactivate)
 *   - suggestionLoading: bloqueia o botão "Liberar" enquanto carrega
 *     a sugestão IA (UX: queremos mostrar a sugestão no modal antes
 *     de ativar)
 *   - onActivate: dispara fluxo de ativação (abre OnboardingModal)
 *   - onInactivate: dispara Alert.confirm + inativação
 *
 * Quando NÃO usar: este componente é específico do fluxo
 * waiting→active→inactive de alunos. Não use pra outros estados.
 */

import { memo } from 'react';
import { Text, TouchableOpacity, StyleSheet } from 'react-native';

interface Props {
  status: 'waiting' | 'active' | 'inactive';
  activating: boolean;
  suggestionLoading: boolean;
  onActivate: () => void;
  onInactivate: () => void;
}

function StatusActionsImpl({ status, activating, suggestionLoading, onActivate, onInactivate }: Props) {
  if (status === 'waiting') {
    return (
      <TouchableOpacity
        style={[styles.activate, suggestionLoading && { opacity: 0.6 }]}
        onPress={onActivate}
        disabled={activating || suggestionLoading}
      >
        <Text style={styles.activateText}>
          {activating ? 'Liberando...' : suggestionLoading ? 'Aguarde...' : 'Liberar app para o aluno'}
        </Text>
      </TouchableOpacity>
    );
  }

  if (status === 'active') {
    return (
      <TouchableOpacity
        style={styles.inactivate}
        onPress={onInactivate}
        disabled={activating}
      >
        <Text style={styles.inactivateText}>Inativar aluno</Text>
      </TouchableOpacity>
    );
  }

  return null;
}

const styles = StyleSheet.create({
  activate: {
    backgroundColor: '#2E7D32',
    padding: 16,
    borderRadius: 10,
    alignItems: 'center',
    marginTop: 16,
  },
  activateText: { color: '#fff', fontWeight: 'bold', fontSize: 15 },
  inactivate: { padding: 12, alignItems: 'center', marginTop: 8 },
  inactivateText: { color: '#aaa', fontSize: 13 },
});

export const StatusActions = memo(StatusActionsImpl);
